import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, FormControl, FormLabel, Grid, GridItem, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Select } from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { getPedestalDecorationOptions, getPedestalPricingConfig } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { PEDESTAL_STYLES, applyNoEmcFabricationMultiplier } from 'lib'

export const EdgePedestalForm = ({ form, setForm, ...rest }) => {
  const { createToast } = useAppContext()
  const [pedestalConfig, setPedestalConfig] = useState({
    pedestalBase: 0,
    pedestalPerSqin: 0,
    polecoverBase: 0,
    polecoverPerSqin: 0,
    pedestalskinsBase: 0,
    pedestalskinsPerSqin: 0
  })

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPedestalPricingConfig()
        setPedestalConfig(res)
      } catch (err) {
        createToast('error', 'Error', err.response.data)
      }
    }
    load()
  }, [])

  useEffect(() => {
    let pedestalCost = 0
    if (form.pedestalHeightIn > 0 && form.pedestalLengthIn > 0) {
      if (form.pedestalStyle === PEDESTAL_STYLES.Pedestal) {
        pedestalCost += pedestalConfig.pedestalBase + pedestalConfig.pedestalPerSqin * form.pedestalHeightIn * form.pedestalLengthIn
      } else if (form.pedestalStyle === PEDESTAL_STYLES.PoleCover) {
        pedestalCost += pedestalConfig.polecoverBase + pedestalConfig.polecoverPerSqin * form.pedestalHeightIn * form.pedestalLengthIn
      } else if (form.pedestalStyle === PEDESTAL_STYLES.PedestalSkins) {
        pedestalCost += pedestalConfig.pedestalskinsBase + pedestalConfig.pedestalskinsPerSqin * form.pedestalHeightIn * form.pedestalLengthIn
      } else {
        // None
      }
    }
    if (form.pedestalDecorationOption) {
      pedestalCost += form.pedestalDecorationOption.cost
    }
    pedestalCost = applyNoEmcFabricationMultiplier(pedestalCost, form.noEmc)
    setForm(draft => {
      draft.pedestalCost = parseFloat(pedestalCost.toFixed(2))
    })
  }, [form.pedestalStyle, form.pedestalHeightIn, form.pedestalLengthIn, form.pedestalDecorationOption?.id, form.noEmc, pedestalConfig])

  const loadDecorationOptions = useCallback((page) => getPedestalDecorationOptions(page, 20), [])

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md">Pedestal / Pole Cover</Heading>
            <Checkbox
              borderColor="gray.400"
              isChecked={form.noPedestal}
              onChange={(e) => setForm(draft => {
                draft.noPedestal = e.target.checked
                if (draft.noPedestal) {
                  draft.pedestalStyle = ''
                  draft.pedestalHeightIn = 0
                  draft.pedestalLengthIn = 0
                  draft.pedestalDecorationOption = null
                  draft.pedestalCost = 0
                } else {
                  draft.pedestalAdditionalHeight = 0
                }
              })}
            >
              No Pedestal
            </Checkbox>
          </Box>
        </GridItem>
        <GridItem></GridItem>
        {form.noPedestal ? (
          <FormControl isRequired>
            <FormLabel>Additional Pole Height (in)</FormLabel>
            <NumberInput
              onChange={v => setForm(draft => { draft.pedestalAdditionalHeight = parseInt(v) })}
              value={form.pedestalAdditionalHeight}
              min={0}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        ) : (
          <>
            <GridItem>
              <Grid
                templateColumns="repeat(3, 1fr)"
                gap={4}
              >
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Style</FormLabel>
                    <Select
                      value={form.pedestalStyle}
                      onChange={e => setForm(draft => { draft.pedestalStyle = e.target.value })}
                      placeholder="Select"
                    >
                      <option value={PEDESTAL_STYLES.Pedestal}>{PEDESTAL_STYLES.Pedestal}</option>
                      <option value={PEDESTAL_STYLES.PoleCover}>{PEDESTAL_STYLES.PoleCover}</option>
                      <option value={PEDESTAL_STYLES.PedestalSkins}>{PEDESTAL_STYLES.PedestalSkins}</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Height (in)</FormLabel>
                    <NumberInput
                      onChange={v => setForm(draft => { draft.pedestalHeightIn = parseInt(v) })}
                      value={form.pedestalHeightIn}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Width (in)</FormLabel>
                    <NumberInput
                      onChange={v => { setForm(draft => { draft.pedestalLengthIn = parseInt(v) }) }}
                      value={form.pedestalLengthIn}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl isRequired>
                    <FormLabel>Decoration options</FormLabel>
                    <UiSelect
                      value={form.pedestalDecorationOption}
                      onChange={v => { setForm(draft => { draft.pedestalDecorationOption = v }) }}
                      loadData={loadDecorationOptions}
                      apiRespProp="rows"
                      getOptionValue={v => v.id}
                      getOptionLabel={v => `${v.description} - ${v.cost}`}
                      placeholder="Search"
                      noOptionsMessage={() => 'No Pedestal Options Found'}
                      isSearchable={false}
                      required={true}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Pedestal Total</FormLabel>
                <Input name="pedestal-total" value={form.pedestalCost} readOnly />
              </FormControl>
            </GridItem>
          </>
        )}
      </Grid>
    </Box>
  )
}

EdgePedestalForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
